import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Code2, Network, BarChart3, Users, Server,
  ArrowRight, CheckCircle, Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Building2,
  Code2,
  Network,
  BarChart3,
  Users,
  Server,
};

const processSteps = [
  {
    number: '01',
    title: 'Discovery & Analysis',
    description: 'Understanding your requirements, challenges, and objectives through detailed consultations.'
  },
  {
    number: '02',
    title: 'Solution Design',
    description: 'Architecting tailored solutions that align with your technical and business needs.'
  },
  {
    number: '03',
    title: 'Development & Testing',
    description: 'Agile development with rigorous testing to ensure quality and compliance.'
  },
  {
    number: '04',
    title: 'Deployment & Support',
    description: 'Seamless deployment with ongoing maintenance and 24/7 support.'
  }
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-nstu-green-800" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-nstu-gold-400 text-nstu-green-900 mb-6">Our Services</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Comprehensive IT Solutions for Government & Enterprise
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              From e-Governance platforms to enterprise software development, we offer end-to-end 
              technology services designed to meet the unique requirements of public sector and 
              corporate clients.
            </p>
            <Link to="/contact">
              <Button className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="services-cta-btn">
                Request a Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-green-100 text-nstu-green-800 mb-4">What We Offer</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-4">
              Our Service Portfolio
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Specialized services tailored for government digital transformation and enterprise needs
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gray-200 rounded-xl mb-6" />
                    <div className="h-6 bg-gray-200 rounded mb-3 w-3/4" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => {
                const IconComponent = iconMap[service.icon] || Server;
                return (
                  <Card 
                    key={service.id}
                    className="group border border-gray-100 hover:border-nstu-gold-400 transition-all duration-300 card-hover overflow-hidden"
                    data-testid={`service-detail-${service.id}`}
                  >
                    <CardContent className="p-8">
                      <div className="w-14 h-14 rounded-xl bg-nstu-green-800 flex items-center justify-center mb-6 group-hover:bg-nstu-gold-400 transition-colors">
                        <IconComponent className="w-7 h-7 text-white group-hover:text-nstu-green-900" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-nstu-green-800 mb-4">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {service.description}
                      </p>
                      <div className="border-t pt-6">
                        <h4 className="text-sm font-semibold text-nstu-green-800 mb-3">Key Capabilities:</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 text-nstu-gold-500 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Government Focus Section */}
      <section className="py-20 bg-nstu-green-800" data-testid="gov-focus">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Landmark className="w-8 h-8 text-nstu-gold-400" />
                <Badge className="bg-nstu-gold-400 text-nstu-green-900">Government Specialist</Badge>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Specialized in Government IT Projects
              </h2>
              <p className="text-gray-300 mb-8">
                With extensive experience in government sector projects, we understand the unique 
                requirements of public sector digitization including security compliance, data 
                sovereignty, accessibility standards, and citizen-centric design principles.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'e-Governance portal development',
                  'Citizen service platforms',
                  'Document management systems',
                  'Grievance redressal systems',
                  'Digital payment integration',
                  'Data analytics dashboards'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-nstu-gold-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/government-projects">
                <Button className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="view-gov-projects-btn">
                  View Government Projects
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760872646618-13594fc00567?w=800"
                alt="Indian Government Building"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-nstu-green-800 flex items-center justify-center">
                    <Landmark className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-nstu-green-800 text-2xl">50+</p>
                    <p className="text-sm text-gray-600">Govt. Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50" data-testid="process">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-gold-100 text-nstu-gold-700 mb-4">Our Approach</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-4">
              How We Deliver Excellence
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A proven methodology that ensures successful project delivery every time
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.number} className="relative">
                <div className="text-6xl font-bold text-nstu-green-100 mb-4">{step.number}</div>
                <h3 className="font-heading text-xl font-bold text-nstu-green-800 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-1/2 h-0.5 bg-nstu-gold-400" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white" data-testid="technologies">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-green-100 text-nstu-green-800 mb-4">Technology Stack</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-4">
              Technologies We Work With
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              'React', 'Angular', 'Vue.js', 'Node.js', 'Python', 'Java',
              '.NET', 'PostgreSQL', 'MongoDB', 'AWS', 'Azure', 'Docker',
              'Kubernetes', 'TensorFlow', 'Power BI', 'SAP', 'Oracle', 'Redis'
            ].map((tech) => (
              <div 
                key={tech}
                className="bg-gray-50 rounded-lg p-4 text-center hover:bg-nstu-green-50 hover:shadow-md transition-all cursor-default"
              >
                <span className="font-medium text-gray-700">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-nstu-green-800" data-testid="services-bottom-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-gray-300 mb-8">
            Our team of experts is ready to understand your requirements and propose the right solution
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-semibold btn-lift">
                Request a Proposal
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:+919541377777">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-nstu-green-800">
                Call +91 95413 77777
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
